import { BaseGenerator } from './BaseGenerator.js';
import { ZONE } from './ZoneTypes.js';
import { MapPipeline } from './MapPipeline.js';

/**
 * Cave map generator using cellular automata.
 * Produces organic cave systems with optional water pools and crystal formations.
 *
 * Supports three quality modes:
 * - "draft"    — direct tile ID output (legacy, fast)
 * - "standard" — pipeline with transition + cleanup + validation (region connectivity)
 * - "high"     — pipeline + full decoration (crystals, stalagmites near walls)
 */
export class CaveGenerator extends BaseGenerator {
  generate(content) {
    const quality = this.options.quality || 'draft';

    if (quality === 'draft') {
      return this._generateDraft(content);
    }

    return this._generatePipeline(content, quality);
  }

  // ---- Pipeline mode (standard / high) ----

  _generatePipeline(content, quality) {
    const tiles = content.tiles || CaveGenerator.DEFAULT_TILES;
    const opts = { ...CaveGenerator.DEFAULT_OPTIONS, ...this.options };

    // 1. Structure pass — cellular automata produces zone map
    let ground = this.createGrid(this.width, this.height, ZONE.WALL);
    const objects = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const overhead = this.createGrid(this.width, this.height, ZONE.EMPTY);

    // Initialize random grid
    for (let y = 1; y < this.height - 1; y++) {
      for (let x = 1; x < this.width - 1; x++) {
        if (this.rng.chance(opts.initialOpenChance)) {
          ground[y][x] = ZONE.FLOOR;
        }
      }
    }

    // Cellular automata iterations
    for (let i = 0; i < opts.iterations; i++) {
      ground = this._iterateZone(ground, opts);
    }

    // Ensure borders are walls
    for (let x = 0; x < this.width; x++) {
      ground[0][x] = ZONE.WALL;
      ground[this.height - 1][x] = ZONE.WALL;
    }
    for (let y = 0; y < this.height; y++) {
      ground[y][0] = ZONE.WALL;
      ground[y][this.width - 1] = ZONE.WALL;
    }

    // Connect disconnected regions
    this._connectRegionsZone(ground);

    // Water pools on ground layer
    if (opts.water) {
      this._addWaterPoolsZone(ground);
    }

    // 2. Run pipeline
    const pipeline = MapPipeline.createDefault(this.rng, { tileMeta: this.options.tileMeta });

    if (quality !== 'high') {
      pipeline.removePass('decoration');
    }

    const pipelineOptions = {
      tileMapping: this._buildTileMapping(tiles),
      floorTileIds: [ZONE.FLOOR, ZONE.WATER],
      corridorTileId: ZONE.FLOOR,
      validation: {
        floorTileIds: [ZONE.FLOOR, ZONE.WATER],
        corridorTileId: ZONE.FLOOR,
        repair: true,
        ensureExits: false,
      },
    };

    if (quality === 'high') {
      pipelineOptions.decorations = this._buildDecorationSpecs(tiles, opts);
      pipelineOptions.edgeDecorations = [
        {
          edgeTileId: ZONE.FLOOR,
          decorations: [
            { tileId: ZONE.CRYSTAL, chance: 0.06, spacing: 4 },
            { tileId: ZONE.STALAGMITE, chance: 0.08, spacing: 3 },
          ],
        },
      ];
    }

    const result = pipeline.run({
      ground,
      objects,
      overhead,
      zones: ground,
      width: this.width,
      height: this.height,
      metadata: {},
    }, pipelineOptions);

    // 3. Resolve zones to tile IDs
    const finalGround = this._resolveZones(result.ground, tiles);
    this._addWallVariation(finalGround, tiles);
    const finalObjects = this._resolveObjectZones(result.objects, tiles);
    const finalOverhead = result.overhead.map(row => [...row]);

    // Collision
    const collision = finalGround.map(row =>
      row.map(tile => tile === tiles.wall || (tiles.wallVariants || []).includes(tile) ? 1 : 0)
    );

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground: finalGround, objects: finalObjects, overhead: finalOverhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(finalGround) },
        { name: 'Objects', data: this.flattenGrid(finalObjects) },
        { name: 'Overhead', data: this.flattenGrid(finalOverhead) },
      ],
      tilesets: content.tilesets || [],
      properties: {
        collision: { type: 'string', value: JSON.stringify(this.flattenGrid(collision)) },
      },
    });
  }

  _iterateZone(grid, opts) {
    const newGrid = this.createGrid(this.width, this.height, ZONE.WALL);

    for (let y = 1; y < this.height - 1; y++) {
      for (let x = 1; x < this.width - 1; x++) {
        const wallCount = this.countNeighbors(grid, x, y, v => v === ZONE.WALL, true);

        if (grid[y][x] === ZONE.WALL) {
          newGrid[y][x] = wallCount >= opts.birthThreshold ? ZONE.WALL : ZONE.FLOOR;
        } else {
          newGrid[y][x] = wallCount >= opts.deathThreshold ? ZONE.WALL : ZONE.FLOOR;
        }
      }
    }

    return newGrid;
  }

  _addWaterPoolsZone(ground) {
    const poolCount = this.rng.nextInt(1, 4);

    for (let i = 0; i < poolCount; i++) {
      for (let a = 0; a < 50; a++) {
        const x = this.rng.nextInt(3, this.width - 4);
        const y = this.rng.nextInt(3, this.height - 4);
        if (ground[y][x] !== ZONE.FLOOR) continue;

        const radius = this.rng.nextInt(1, 3);
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            if (dx * dx + dy * dy <= radius * radius + this.rng.nextFloat(-1, 1)) {
              const px = x + dx, py = y + dy;
              if (py > 0 && py < this.height - 1 && px > 0 && px < this.width - 1) {
                if (ground[py][px] === ZONE.FLOOR) {
                  ground[py][px] = ZONE.WATER;
                }
              }
            }
          }
        }
        break;
      }
    }
  }

  _connectRegionsZone(ground) {
    const visited = this.createGrid(this.width, this.height, 0);
    const regions = [];

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (ground[y][x] === ZONE.FLOOR && visited[y][x] === 0) {
          const region = [];
          const queue = [[x, y]];
          visited[y][x] = 1;
          while (queue.length > 0) {
            const [cx, cy] = queue.pop();
            region.push({ x: cx, y: cy });
            for (const n of this.getNeighbors4(cx, cy)) {
              if (n.x >= 0 && n.x < this.width && n.y >= 0 && n.y < this.height) {
                if (ground[n.y][n.x] === ZONE.FLOOR && visited[n.y][n.x] === 0) {
                  visited[n.y][n.x] = 1;
                  queue.push([n.x, n.y]);
                }
              }
            }
          }
          regions.push(region);
        }
      }
    }

    if (regions.length <= 1) return;
    regions.sort((a, b) => b.length - a.length);

    for (let i = 1; i < regions.length; i++) {
      if (regions[i].length < 10) {
        for (const cell of regions[i]) ground[cell.y][cell.x] = ZONE.WALL;
        continue;
      }
      const mainCell = this.rng.pick(regions[0]);
      let closest = regions[i][0];
      let minDist = Infinity;
      for (const cell of regions[i]) {
        const dist = Math.abs(cell.x - mainCell.x) + Math.abs(cell.y - mainCell.y);
        if (dist < minDist) { minDist = dist; closest = cell; }
      }
      this.drawLine(ground, closest.x, closest.y, mainCell.x, mainCell.y, ZONE.FLOOR, 2);
    }
  }

  _buildTileMapping(tiles) {
    return {
      [ZONE.WALL]:  { center: tiles.wall },
      [ZONE.FLOOR]: { center: tiles.floor },
      [ZONE.WATER]: { center: tiles.water },
    };
  }

  _buildDecorationSpecs(tiles, opts) {
    const specs = [];
    if (tiles.stalagmite) {
      specs.push({
        tileId: ZONE.STALAGMITE,
        density: 0.008,
        spacing: 3,
        validGround: [ZONE.FLOOR],
        avoidEdges: true,
      });
    }
    return specs;
  }

  _resolveZones(grid, tiles) {
    const mapping = {
      [ZONE.WALL]:  tiles.wall,
      [ZONE.FLOOR]: tiles.floor,
      [ZONE.WATER]: tiles.water || tiles.floor,
    };
    return grid.map(row => row.map(z => {
      if (mapping[z] !== undefined) return mapping[z];
      return z;
    }));
  }

  _resolveObjectZones(grid, tiles) {
    const crystalTiles = tiles.crystals || (tiles.crystal ? [tiles.crystal] : [0]);
    const stalagTiles = tiles.stalagmites || (tiles.stalagmite ? [tiles.stalagmite] : [0]);
    return grid.map(row => row.map(z => {
      if (z === ZONE.EMPTY || z === 0) return 0;
      if (z === ZONE.CRYSTAL) return this.rng.pick(crystalTiles);
      if (z === ZONE.STALAGMITE) return this.rng.pick(stalagTiles);
      return z;
    }));
  }

  // ---- Draft mode (original direct tile output) ----

  _generateDraft(content) {
    const tiles = content.tiles || CaveGenerator.DEFAULT_TILES;
    const opts = { ...CaveGenerator.DEFAULT_OPTIONS, ...this.options };

    // 1. Initialize random grid
    let cave = this.createGrid(this.width, this.height, tiles.wall);
    for (let y = 1; y < this.height - 1; y++) {
      for (let x = 1; x < this.width - 1; x++) {
        if (this.rng.chance(opts.initialOpenChance)) {
          cave[y][x] = tiles.floor;
        }
      }
    }

    // 2. Cellular automata iterations
    for (let i = 0; i < opts.iterations; i++) {
      cave = this._iterate(cave, tiles, opts);
    }

    // 3. Ensure borders are walls
    for (let x = 0; x < this.width; x++) {
      cave[0][x] = tiles.wall;
      cave[this.height - 1][x] = tiles.wall;
    }
    for (let y = 0; y < this.height; y++) {
      cave[y][0] = tiles.wall;
      cave[y][this.width - 1] = tiles.wall;
    }

    // 4. Connect disconnected regions
    this._connectRegions(cave, tiles);

    // 5. Add features
    const objects = this.createGrid(this.width, this.height, 0);
    const overhead = this.createGrid(this.width, this.height, 0);

    if (opts.water) {
      this._addWaterPools(cave, tiles);
    }

    if (opts.crystals) {
      this._addCrystals(objects, cave, tiles);
    }

    if (opts.stalagmites) {
      this._addStalagmites(objects, cave, tiles);
    }

    // 6. Wall variation
    this._addWallVariation(cave, tiles);

    // 7. Collision
    const collision = cave.map(row =>
      row.map(tile => tile === tiles.wall || (tiles.wallVariants || []).includes(tile) ? 1 : 0)
    );

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground: cave, objects, overhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(cave) },
        { name: 'Objects', data: this.flattenGrid(objects) },
        { name: 'Overhead', data: this.flattenGrid(overhead) },
      ],
      tilesets: content.tilesets || [],
      properties: {
        collision: { type: 'string', value: JSON.stringify(this.flattenGrid(collision)) },
      },
    });
  }

  // ---- Original helpers (used by draft mode) ----

  _iterate(grid, tiles, opts) {
    const newGrid = this.createGrid(this.width, this.height, tiles.wall);

    for (let y = 1; y < this.height - 1; y++) {
      for (let x = 1; x < this.width - 1; x++) {
        const wallCount = this.countNeighbors(grid, x, y, v => v === tiles.wall, true);

        if (grid[y][x] === tiles.wall) {
          newGrid[y][x] = wallCount >= opts.birthThreshold ? tiles.wall : tiles.floor;
        } else {
          newGrid[y][x] = wallCount >= opts.deathThreshold ? tiles.wall : tiles.floor;
        }
      }
    }

    return newGrid;
  }

  _connectRegions(cave, tiles) {
    const visited = this.createGrid(this.width, this.height, 0);
    const regions = [];

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (cave[y][x] === tiles.floor && visited[y][x] === 0) {
          const region = [];
          const queue = [[x, y]];
          visited[y][x] = 1;

          while (queue.length > 0) {
            const [cx, cy] = queue.pop();
            region.push({ x: cx, y: cy });

            for (const n of this.getNeighbors4(cx, cy)) {
              if (n.x >= 0 && n.x < this.width && n.y >= 0 && n.y < this.height) {
                if (cave[n.y][n.x] === tiles.floor && visited[n.y][n.x] === 0) {
                  visited[n.y][n.x] = 1;
                  queue.push([n.x, n.y]);
                }
              }
            }
          }

          regions.push(region);
        }
      }
    }

    if (regions.length <= 1) return;

    regions.sort((a, b) => b.length - a.length);

    for (let i = 1; i < regions.length; i++) {
      if (regions[i].length < 10) {
        for (const cell of regions[i]) {
          cave[cell.y][cell.x] = tiles.wall;
        }
        continue;
      }

      const mainCell = this.rng.pick(regions[0]);
      let closest = regions[i][0];
      let minDist = Infinity;
      for (const cell of regions[i]) {
        const dist = Math.abs(cell.x - mainCell.x) + Math.abs(cell.y - mainCell.y);
        if (dist < minDist) {
          minDist = dist;
          closest = cell;
        }
      }

      this.drawLine(cave, closest.x, closest.y, mainCell.x, mainCell.y, tiles.floor, 2);
    }
  }

  _addWaterPools(cave, tiles) {
    if (!tiles.water) return;
    const poolCount = this.rng.nextInt(1, 4);

    for (let i = 0; i < poolCount; i++) {
      for (let a = 0; a < 50; a++) {
        const x = this.rng.nextInt(3, this.width - 4);
        const y = this.rng.nextInt(3, this.height - 4);
        if (cave[y][x] !== tiles.floor) continue;

        const radius = this.rng.nextInt(1, 3);
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            if (dx * dx + dy * dy <= radius * radius + this.rng.nextFloat(-1, 1)) {
              const px = x + dx, py = y + dy;
              if (py > 0 && py < this.height - 1 && px > 0 && px < this.width - 1) {
                if (cave[py][px] === tiles.floor) {
                  cave[py][px] = tiles.water;
                }
              }
            }
          }
        }
        break;
      }
    }
  }

  _addCrystals(objects, cave, tiles) {
    if (!tiles.crystal) return;
    const count = Math.floor(this.width * this.height * 0.005);

    for (let i = 0; i < count; i++) {
      const x = this.rng.nextInt(1, this.width - 2);
      const y = this.rng.nextInt(1, this.height - 2);
      if (cave[y][x] !== tiles.floor || objects[y][x] !== 0) continue;

      const nearWall = this.countNeighbors(cave, x, y, v => v === tiles.wall, true);
      if (nearWall >= 2) {
        const crystalTiles = tiles.crystals || [tiles.crystal];
        this.setCell(objects, x, y, this.rng.pick(crystalTiles));
      }
    }
  }

  _addStalagmites(objects, cave, tiles) {
    if (!tiles.stalagmite) return;
    const count = Math.floor(this.width * this.height * 0.008);

    for (let i = 0; i < count; i++) {
      const x = this.rng.nextInt(1, this.width - 2);
      const y = this.rng.nextInt(1, this.height - 2);
      if (cave[y][x] !== tiles.floor || objects[y][x] !== 0) continue;

      const stalagTiles = tiles.stalagmites || [tiles.stalagmite];
      this.setCell(objects, x, y, this.rng.pick(stalagTiles));
    }
  }

  _addWallVariation(cave, tiles) {
    if (!tiles.wallVariants || tiles.wallVariants.length === 0) return;

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (cave[y][x] === tiles.wall && this.rng.chance(0.15)) {
          cave[y][x] = this.rng.pick(tiles.wallVariants);
        }
      }
    }
  }

  static DEFAULT_TILES = {
    wall: 1,
    wallVariants: [1, 2],
    floor: 3,
    water: 10,
    crystal: 20,
    crystals: [20, 21],
    stalagmite: 22,
    stalagmites: [22, 23],
  };

  static DEFAULT_OPTIONS = {
    initialOpenChance: 0.45,
    iterations: 5,
    birthThreshold: 4,
    deathThreshold: 5,
    water: true,
    crystals: true,
    stalagmites: true,
  };

  static getEmittedZones() {
    return [
      ZONE.WALL, ZONE.FLOOR, ZONE.WATER, ZONE.CRYSTAL, ZONE.STALAGMITE,
    ];
  }
}
